import React, { useState, useEffect, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Placeholder from "../../assets/images/placeholder.png";
import DeleteIcon from "../../assets/images/icons/delete-w.svg";
import { Link } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../utils/constants";
import editImage from "../../assets/images/icons/edit-2.svg";
import Modal from "../../commons/Modal/Modal";
import SimplePagePagination from "../../commons/Pagination/SimplePagePagination";
import BannerPreviewModal from "./BannerPreviewModal";
function BannerManagement() {
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logout, showLogout] = useState(false);
  const [logoutModal, setLogoutModal] = useState({ value: false });
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewBanner, setPreviewBanner] = useState(null);

  const getBanners = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await handleApiCall("get", `${endpoints.bannerList}?${params}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setBanners(response?.data?.data?.data || []);
        setTotalPages(response?.data?.data?.pagination?.totalPages || 1);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching banners:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    getBanners();
  }, [getBanners]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusToggle = async (bannerId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;

      const response = await handleApiCall("patch", `${endpoints.updatestatus}/${bannerId}`, {
        status: newStatus,
      });

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(UI_CONSTANTS.TOAST_MESSAGES.SUCCESS.STATUS_UPDATED);
        getBanners(); // Refresh the list
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error updating banner status:", error);
      // Network errors are already handled by handleApiCall
    }
  };

  const handleDeleteClick = (bannerId) => {
    setBannerToDelete(bannerId);
    setLogoutModal({ value: true });
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;

    setDeleting(true);
    try {
      const response = await handleApiCall("delete", `${endpoints.deleteBanner}/${bannerToDelete}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(UI_CONSTANTS.TOAST_MESSAGES.SUCCESS.BANNER_DELETED);
        setLogoutModal({ value: false });
        setBannerToDelete(null);
        getBanners(); // Refresh the list
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error deleting banner:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setLogoutModal({ value: false });
    setBannerToDelete(null);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">{UI_CONSTANTS.PAGE_TITLES.BANNER_MANAGEMENT}</h3>
              <Link to="/add-banner" className="btn btn-primary rounded-2 px-3 ml-auto">
                {UI_CONSTANTS.BUTTON_LABELS.CREATE_NEW} <span className="ml-2">+</span>
              </Link>
            </div>

            {/* Search Bar */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control search-icon left-icon border"
                    placeholder={UI_CONSTANTS.PLACEHOLDERS.SEARCH}
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>
            </div>

            <div className="table-responsive px-0">
              <table className="table table-striped trim">
                <thead>
                  <tr>
                    <th width="80">{UI_CONSTANTS.TABLE_HEADERS.SR_NO}</th>
                    <th width="150">{UI_CONSTANTS.TABLE_HEADERS.NAME}</th>
                    <th>{UI_CONSTANTS.TABLE_HEADERS.IMAGE}</th>
                    <th>{UI_CONSTANTS.TABLE_HEADERS.STATUS}</th>
                    <th className="text-center">{UI_CONSTANTS.TABLE_HEADERS.ACTIONS}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
                        </div>
                      </td>
                    </tr>
                  ) : banners.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                      </td>
                    </tr>
                  ) : (
                    banners.map((banner, index) => (
                      <tr key={banner._id}>
                        <td>{(currentPage - 1) * 10 + index + 1}</td>
                        <td className="text-center" >{banner.title || UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}</td>
                        <td>
                          <button
                            type="button"
                            className="banner-list-image-btn"
                            onClick={() => setPreviewBanner(banner)}
                          >
                            <img
                              src={banner.image || Placeholder}
                              alt={UI_CONSTANTS.TABLE_HEADERS.IMAGE}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = Placeholder;
                              }}
                            />
                          </button>
                        </td>
                        <td>
                          <label className="custom-control custom-switch p-1 ml-4 mb-0 mt-1" role="button">
                            <input type="checkbox" className="custom-control-input" checked={banner.status === 1} onChange={() => handleStatusToggle(banner._id, banner.status)} />
                            <span className="custom-control-label custom-toggle" />
                          </label>
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center">
                            <Link to={`/edit-banner/${banner._id}`} className="btn btn-link p-0 mx-2">
                              <img src={editImage} alt={UI_CONSTANTS.BUTTON_LABELS.EDIT} className="mr-2" />
                            </Link>
                            <button className="btn btn-link p-0 mx-2" onClick={() => handleDeleteClick(banner._id)}>
                              <img src={DeleteIcon} alt={UI_CONSTANTS.BUTTON_LABELS.DELETE} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <SimplePagePagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="d-flex justify-content-center mt-4"
            />
          </div>
        </div>
      </div>
      <Modal show={logoutModal.value} onClose={handleDeleteCancel} style={{ zIndex: "1000" }} className=" shadow-none">
        <div className="modal-dialog modal-md modal-center" role="document">
          <div className="modal-content bg-black border-light">
            <div className="modal-body text-center my-4 py-4">
              <h4 className="text-white">
                Do you want to
                <br />
                Delete this banner?
              </h4>
              <div className="col-sm-12 mx-auto d-flex align-items-center mt-4 row">
                <div className="col-6 px-2">
                  <button type="button" className="btn btn-primary btn-lg btn-block font-16 rounded-pill" onClick={handleDeleteConfirm} disabled={deleting}>
                    {deleting ? "Deleting..." : "Yes"}
                  </button>
                </div>
                <div className="col-6 px-2">
                  <button type="button" className="btn btn-default btn-lg btn-block font-16 rounded-pill" onClick={handleDeleteCancel} disabled={deleting}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <BannerPreviewModal
        show={Boolean(previewBanner)}
        onClose={() => setPreviewBanner(null)}
        title={previewBanner?.title}
        description={previewBanner?.description}
        image={previewBanner?.image || Placeholder}
      />
    </div>
  );
}

export default BannerManagement;
